import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Camera, Check, X, Loader2, MapPin, Star, Calendar, ExternalLink, RefreshCw, ChevronDown, ChevronUp, Zap } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

/**
 * Admin moderation queue for Trip Wall photos that the user opted in to share
 * on the campsite page. Approving pushes the photo into campsites.images;
 * rejecting only flags the photo's campsite_status — the trip story itself
 * stays public on the Wall regardless.
 */
export default function PhotoModerationCard() {
  const [stories, setStories] = useState([]);
  const [submissions, setSubmissions] = useState([]); // direct campsite-page uploads
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({}); // key = `${story_id}:${idx}` or `direct:${photo_id}` → bool
  const [expanded, setExpanded] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [w, d] = await Promise.all([
        api.get("/admin/wall/photos/pending"),
        api.get("/admin/photos/pending"),
      ]);
      setStories(w.data.items || []);
      setSubmissions(d.data.items || []);
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const act = async (storyId, idx, action) => {
    const key = `${storyId}:${idx}`;
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      await api.post(`/admin/wall/photos/${storyId}/${idx}/${action}`);
      toast.success(action === "approve"
        ? "Photo published on the campsite page"
        : "Photo hidden from campsite page");
      // Optimistically remove this photo from local view
      setStories((prev) => prev.map((s) => {
        if (s.story_id !== storyId) return s;
        const photos = (s.photos || []).map((p, i) =>
          i === idx ? { ...p, campsite_status: action === "approve" ? "approved" : "rejected" } : p
        );
        return { ...s, photos };
      // Drop story if no pending photos remain
      }).filter((s) => (s.photos || []).some((p) => p.campsite_status === "pending")));
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy((b) => { const next = { ...b }; delete next[key]; return next; });
    }
  };

  const actDirect = async (photoId, action) => {
    const key = `direct:${photoId}`;
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      await api.post(`/admin/photos/${photoId}/${action}`);
      toast.success(action === "approve"
        ? "Photo published on the campsite page"
        : "Photo rejected");
      setSubmissions((prev) => prev.filter((s) => s.photo_id !== photoId));
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy((b) => { const next = { ...b }; delete next[key]; return next; });
    }
  };

  const totalPending = stories.reduce((acc, s) => acc + (s.photos || []).filter((p) => p.campsite_status === "pending").length, 0)
    + submissions.length;

  const approveAll = async () => {
    if (!totalPending) return;
    if (!window.confirm(`Approve ALL ${totalPending} pending photos in one go? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/admin/photos/approve-all`);
      const total = (data.approved || 0) + (data.trip_photos_approved || 0);
      toast.success(`Approved ${total} photo${total === 1 ? "" : "s"} — queue cleared.`);
      setStories([]);
      setSubmissions([]);
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setBulkBusy(false); }
  };

  return (
    <div className="border hairline rounded-2xl bg-white p-5 mb-6" data-testid="photo-moderation-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-2 text-left min-w-0"
          data-testid="photo-mod-toggle"
        >
          <Camera className="w-4 h-4 text-[#ff4500] shrink-0" />
          <div>
            <div className="font-display text-base sm:text-lg tracking-tight">
              Photo moderation
            </div>
            <div className="text-xs text-[#666]">
              {loading ? "Loading…" : `${totalPending} pending`}
              {totalPending > 0 && <> · tap to {expanded ? "collapse" : "expand"}</>}
            </div>
          </div>
          {totalPending > 0 && (expanded
            ? <ChevronUp className="w-4 h-4 text-[#666]" />
            : <ChevronDown className="w-4 h-4 text-[#666]" />)}
        </button>

        <div className="flex gap-2">
          {totalPending > 0 && (
            <button
              type="button"
              onClick={approveAll}
              disabled={bulkBusy}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#0a7f3f] text-white text-xs font-black uppercase tracking-wider hover:bg-[#046030] disabled:opacity-60"
              data-testid="photo-mod-approve-all"
            >
              {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
              Approve all {totalPending}
            </button>
          )}
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 text-[11px] mono uppercase tracking-wider px-3 py-2 border hairline rounded-full hover:bg-[#fff4ee]"
            data-testid="photo-mod-refresh"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} strokeWidth={2} />
          </button>
        </div>
      </div>

      {!loading && totalPending === 0 && (
        <div className="text-xs text-[#888] mt-3">
          Inbox zero — no pending photo submissions.
        </div>
      )}

      {expanded && !loading && (stories.length > 0 || submissions.length > 0) && (
        <div className="mt-4">
          <p className="text-xs text-[#666] mb-3 italic">
            Landscape / surroundings only — reject anything with people.
          </p>
          {/* DIRECT campsite-page uploads — shown first */}
          {submissions.length > 0 && (
            <div className="mb-6" data-testid="photo-mod-direct">
              <div className="eyebrow text-[#ff4500] mb-2 flex items-center gap-1.5">
                <Camera className="w-3 h-3" /> Direct uploads · {submissions.length}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {submissions.map((sub) => {
                  const key = `direct:${sub.photo_id}`;
                  const isBusy = !!busy[key];
                  return (
                    <div key={sub.photo_id} className="border hairline overflow-hidden bg-[#111]" data-testid={`photo-mod-direct-${sub.photo_id}`}>
                      <div className="relative">
                        <img
                          src={sub.image_data_url}
                          alt={sub.caption || sub.campsite_name}
                          className="w-full aspect-square object-cover"
                          loading="lazy"
                        />
                        {sub.caption && (
                          <div className="absolute top-0 inset-x-0 bg-black/65 text-white text-[10px] mono px-2 py-1 truncate">
                            {sub.caption}
                          </div>
                        )}
                      </div>
                      <div className="bg-white px-2 py-2 text-[10px] mono leading-tight">
                        <Link to={`/campsites/${sub.campsite_id}`} target="_blank" rel="noopener noreferrer" className="hover:text-[#ff4500] block truncate" title={sub.campsite_name}>
                          {sub.campsite_name}
                        </Link>
                        <div className="text-[#888] truncate">— {sub.user_name}</div>
                      </div>
                      <div className="grid grid-cols-2 gap-px">
                        <button
                          onClick={() => actDirect(sub.photo_id, "approve")}
                          disabled={isBusy}
                          className="inline-flex items-center justify-center gap-1 bg-[#22c55e] text-white text-[11px] mono uppercase tracking-wider py-1.5 hover:bg-[#16a34a] disabled:opacity-60"
                          data-testid={`photo-mod-direct-approve-${sub.photo_id}`}
                        >
                          {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" strokeWidth={2.5} />}
                          Approve
                        </button>
                        <button
                          onClick={() => actDirect(sub.photo_id, "reject")}
                          disabled={isBusy}
                          className="inline-flex items-center justify-center gap-1 bg-black/85 text-white text-[11px] mono uppercase tracking-wider py-1.5 hover:bg-black disabled:opacity-60"
                          data-testid={`photo-mod-direct-reject-${sub.photo_id}`}
                        >
                          <X className="w-3 h-3" strokeWidth={2.5} /> Reject
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* TRIP WALL opt-in photos — second section */}
          {stories.length > 0 && (
            <>
              <div className="eyebrow text-[#888] mb-2 flex items-center gap-1.5">
                Trip Wall opt-ins · {stories.length} {stories.length === 1 ? "story" : "stories"}
              </div>
              <ul className="space-y-5" data-testid="photo-mod-list">
          {stories.map((s) => {
            const pendingPhotos = (s.photos || [])
              .map((p, i) => ({ ...p, idx: i }))
              .filter((p) => p.campsite_status === "pending");
            if (!pendingPhotos.length) return null;
            return (
              <li key={s.story_id} className="border hairline p-4" data-testid={`photo-mod-story-${s.story_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                  <div className="min-w-0">
                    <Link
                      to={`/campsites/${s.campsite_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-display text-base hover:text-[#ff4500] inline-flex items-center gap-1"
                      data-testid={`photo-mod-site-${s.story_id}`}
                    >
                      {s.campsite_name}
                      <ExternalLink className="w-3 h-3" strokeWidth={1.75} />
                    </Link>
                    <div className="flex flex-wrap items-center gap-3 text-[11px] mono text-[#666] mt-1">
                      <span><MapPin className="w-3 h-3 inline mr-0.5" strokeWidth={1.75} />{s.campsite_region}</span>
                      <span>— {s.user_name}</span>
                      <span><Calendar className="w-3 h-3 inline mr-0.5" strokeWidth={1.75} />{fmtDate(s.created_at)}</span>
                      <span className="inline-flex items-center gap-0.5">
                        <Star className="w-3 h-3 fill-[#ff4500] text-[#ff4500]" strokeWidth={1.5} />
                        {s.rating}/5 · {s.nights_stayed} night{s.nights_stayed === 1 ? "" : "s"}
                      </span>
                    </div>
                    {s.highlight && (
                      <p className="text-sm leading-snug text-[#222] mt-2 italic">&ldquo;{s.highlight}&rdquo;</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {pendingPhotos.map((p) => {
                    const key = `${s.story_id}:${p.idx}`;
                    const isBusy = !!busy[key];
                    return (
                      <div key={p.idx} className="relative bg-[#111] border hairline overflow-hidden">
                        <img
                          src={p.image_data_url}
                          alt={`Photo ${p.idx + 1}`}
                          className="w-full aspect-square object-cover"
                          loading="lazy"
                          data-testid={`photo-mod-img-${s.story_id}-${p.idx}`}
                        />
                        {p.caption && (
                          <div className="absolute top-0 inset-x-0 bg-black/60 text-white text-[10px] mono px-2 py-1 truncate">
                            {p.caption}
                          </div>
                        )}
                        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black via-black/85 to-transparent p-2 flex gap-2">
                          <button
                            onClick={() => act(s.story_id, p.idx, "approve")}
                            disabled={isBusy}
                            className="flex-1 inline-flex items-center justify-center gap-1 bg-[#22c55e] text-white text-[11px] mono uppercase tracking-wider py-1.5 hover:bg-[#16a34a] disabled:opacity-60"
                            data-testid={`photo-mod-approve-${s.story_id}-${p.idx}`}
                            aria-label={`Approve photo ${p.idx + 1}`}
                          >
                            {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" strokeWidth={2.5} />}
                            Approve
                          </button>
                          <button
                            onClick={() => act(s.story_id, p.idx, "reject")}
                            disabled={isBusy}
                            className="flex-1 inline-flex items-center justify-center gap-1 bg-black/80 text-white text-[11px] mono uppercase tracking-wider py-1.5 hover:bg-black disabled:opacity-60"
                            data-testid={`photo-mod-reject-${s.story_id}-${p.idx}`}
                            aria-label={`Reject photo ${p.idx + 1}`}
                          >
                            <X className="w-3 h-3" strokeWidth={2.5} /> Reject
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </li>
            );
          })}
        </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
